<template>
  <div>
    <v-dialog v-model="addValueDialog" width="400" persistent>
      <v-card>
        <v-card-title class="grey lighten-2" primary-title>
          {{ $t('components.ModelSettings.addValueTitle') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="addValueText"
            :label="addValueField ? addValueField.label : ''"
            autofocus
            @keyup.enter="confirmAddValue"
          ></v-text-field>
          <v-text-field
            v-model="addValueAbbrText"
            :label="$t('components.ModelSettings.addValueAbbreviationLabel')"
            :error-messages="addValueAbbrHasSpaces ? $t('components.ModelSettings.addValueAbbreviationNoSpaces') : ''"
            @keyup.enter="confirmAddValue"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addValueDialog = false">{{ $t('components.ModelSettings.cancel') }}</v-btn>
          <v-btn color="primary" text :disabled="!canConfirmAddValue" @click="confirmAddValue">{{ $t('components.ModelSettings.addValueButton') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-row class="mt-2">
      <v-col cols="12">
        <v-alert v-if="taxonomyChannelKeys.length === 0" text type="info">
          {{ $t('components.ModelSettings.channelTaxonomyNoChannels') }}
        </v-alert>
        <v-row v-else>
          <v-col cols="12" sm="4" md="3">
            <h3>{{ $t('components.ModelSettings.channelTaxonomyChannels') }}</h3>
            <v-list dense style="max-height: 420px; overflow-y: auto;">
              <v-list-item-group v-model="selectedTaxonomyChannel" mandatory color="primary">
                <v-list-item v-for="channel in taxonomyChannelKeys" :key="channel" :value="channel">
                  <v-list-item-content>
                    <v-list-item-title>{{ channelDisplayName(channel) }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon v-if="channelHasTaxonomy(channel)">
                    <v-icon small color="primary">mdi-circle-medium</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-col>
          <v-col cols="12" sm="8" md="6" v-if="selectedTaxonomyChannel">
            <h3>{{ channelDisplayName(selectedTaxonomyChannel) }}</h3>
            <v-autocomplete
              v-for="level in taxonomySelectLevels"
              :key="selectedTaxonomyChannel + '-' + level"
              v-model="channelTaxonomy[selectedTaxonomyChannel][level]"
              :items="taxonomyOptionsFor(level, selectedTaxonomyChannel)"
              :label="taxonomyLevelLabel(level)"
              :loading="taxonomyLoading"
              :disabled="taxonomyLoading || isLevelDisabled(level, selectedTaxonomyChannel)"
              :hint="dependencyHintFor(level, selectedTaxonomyChannel)"
              persistent-hint
              clearable
              @change="onTaxonomyLevelChange(level)"
            >
              <template v-slot:append-outer>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn icon small v-on="on" :disabled="taxonomyLoading || isLevelDisabled(level, selectedTaxonomyChannel)" @click="openAddValueDialog(level)">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  {{ $t('components.ModelSettings.addValueTooltip') }}
                </v-tooltip>
              </template>
            </v-autocomplete>
            <v-text-field
              v-model="channelTaxonomy[selectedTaxonomyChannel].L6"
              :label="taxonomyLevelLabel('L6')"
              clearable
            ></v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  props: {
    client: {
      type: Object,
      required: true
    },
    // pretty_names.media map from the parent, kept live so channel names follow
    // edits made in the Custom Names tab before saving.
    mediaPrettyNames: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      // { <channelKey>: { L0..L6: '' } } one row per media channel, pre-shaped in
      // created so every cell is reactive without $set.
      channelTaxonomy: {},
      // Channel currently being edited in the taxonomy master-detail view.
      selectedTaxonomyChannel: null,
      // Available values per taxonomy level (L0..L5), loaded from the
      // channel_taxonomy_levels collection to feed the dropdowns. Each value is
      // a { name, abbreviation } object: object[] for independent levels,
      // { <parentName>: object[] } for levels that depend on the previous
      // level's value.
      taxonomyLevelOptions: {},
      // { <level>: boolean } levels flagged in Firestore as depending on the
      // previous level (never L0 or L6); loaded together with the options.
      taxonomyDependsOnPrevious: {},
      // { <level>: object[] } values added via the "Add value" dialog this session
      // for independent levels; merged into the dropdowns and registered on save.
      extraTaxonomyOptions: {},
      // { <level>: { <parent>: object[] } } session-added values for dependent
      // levels, keyed by the parent value they were added under.
      extraTaxonomyOptionsByParent: {},
      // True until the channel_taxonomy_levels registry has finished loading. The
      // selects stay disabled/loading meanwhile so a dropdown can't be opened
      // against an empty option list (Vuetify won't refresh an open menu when the
      // items arrive, which made the list appear empty on the first cold load).
      taxonomyLoading: true,
      addValueDialog: false,
      addValueField: null,
      addValueText: '',
      addValueAbbrText: ''
    }
  },
  methods: {
    ...mapActions('manager', [
      'loadChannelTaxonomyLevels'
    ]),
    isDependentLevel (level) {
      return this.taxonomyDependsOnPrevious[level] === true
    },
    previousLevel (level) {
      return 'L' + (parseInt(level.slice(1), 10) - 1)
    },
    // Value selected in the previous level of the same channel row; the parent
    // key under which a dependent level's options live.
    parentValueFor (level, channel) {
      const row = this.channelTaxonomy[channel]
      return row ? String(row[this.previousLevel(level)] || '').trim() : ''
    },
    // A dependent level cannot be edited until its parent level has a value.
    isLevelDisabled (level, channel) {
      return this.isDependentLevel(level) && !this.parentValueFor(level, channel)
    },
    dependencyHintFor (level, channel) {
      if (!this.isLevelDisabled(level, channel)) {
        return ''
      }
      return this.$t('components.ModelSettings.taxonomySelectParentFirst', {
        parent: this.taxonomyLevelLabel(this.previousLevel(level))
      })
    },
    // Option objects ({ name, abbreviation }) for a taxonomy level: registered
    // values + values added this session, de-duped by name. Dependent levels
    // read both sources under the row's parent value; with no parent selected
    // the list is empty.
    optionObjectsFor (level, channel) {
      let registered, extras
      if (this.isDependentLevel(level)) {
        const parent = this.parentValueFor(level, channel)
        registered = parent ? (((this.taxonomyLevelOptions[level] || {})[parent]) || []) : []
        extras = parent ? (((this.extraTaxonomyOptionsByParent[level] || {})[parent]) || []) : []
      } else {
        registered = this.taxonomyLevelOptions[level] || []
        extras = this.extraTaxonomyOptions[level] || []
      }
      const byName = {}
      registered.concat(extras).forEach(option => {
        byName[option.name] = option
      })
      return Object.keys(byName).map(name => byName[name])
    },
    // Abbreviation for a value name, from the level's options with a fallback to
    // the saved model entry — covering a saved value no longer in the registry
    // (or whose best-effort registration failed on a previous save).
    abbreviationFor (level, channel, name) {
      const match = this.optionObjectsFor(level, channel).find(option => option.name === name)
      if (match) {
        return match.abbreviation || ''
      }
      const savedTaxonomy = this.client.channel_taxonomy
      const savedRow = savedTaxonomy && savedTaxonomy[channel]
      const saved = savedRow && savedRow[level]
      if (saved && typeof saved === 'object' && saved.name === name) {
        return saved.abbreviation || ''
      }
      return ''
    },
    formatOption (option) {
      return option.abbreviation ? option.name + ' (' + option.abbreviation + ')' : option.name
    },
    // Dropdown items for a taxonomy level: the option objects + the current value
    // of that channel's cell (so a pre-existing value always shows even if it is
    // not part of the registry — or, for dependent levels, not listed under the
    // current parent), sorted by name and rendered as "Name (abbr)". The item
    // value is the bare name; Vuetify filters on the text, so typing either the
    // name or the abbreviation finds a value.
    taxonomyOptionsFor (level, channel) {
      const options = this.optionObjectsFor(level, channel)
      const current = this.channelTaxonomy[channel] ? this.channelTaxonomy[channel][level] : ''
      if (current && !options.some(option => option.name === current)) {
        options.push({ name: current, abbreviation: this.abbreviationFor(level, channel, current) })
      }
      return options
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        .map(option => ({ text: this.formatOption(option), value: option.name }))
    },
    // Cascade triggered only by user interaction (@change, which clearable's X
    // also fires): changing a level clears every consecutive dependent level
    // after it, stopping at the first independent one. Never runs on load, so
    // saved combinations that no longer match the registry are left untouched.
    onTaxonomyLevelChange (level) {
      const row = this.channelTaxonomy[this.selectedTaxonomyChannel]
      if (!row) {
        return
      }
      let idx = parseInt(level.slice(1), 10) + 1
      while (idx <= 5) {
        const next = 'L' + idx
        if (!this.isDependentLevel(next)) {
          break
        }
        if (row[next]) {
          row[next] = ''
        }
        idx++
      }
    },
    channelDisplayName (channel) {
      return (this.mediaPrettyNames && this.mediaPrettyNames[channel]) ? this.mediaPrettyNames[channel] : channel
    },
    taxonomyLevelLabel (level) {
      // Hard-coded, translated level names (no longer read from Firestore),
      // shown as "L0 - Name".
      return level + ' - ' + this.$t('components.ModelSettings.taxonomyLevel' + level)
    },
    channelHasTaxonomy (channel) {
      const row = this.channelTaxonomy[channel]
      if (!row) {
        return false
      }
      return Object.keys(row).some(level => String(row[level] || '').trim().length > 0)
    },
    openAddValueDialog (level) {
      if (this.isLevelDisabled(level, this.selectedTaxonomyChannel)) {
        return
      }
      // The parent value is snapshotted here so a parent change between opening
      // and confirming can't register the new value under the wrong parent.
      this.addValueField = {
        level: level,
        channel: this.selectedTaxonomyChannel,
        label: this.taxonomyLevelLabel(level),
        parent: this.isDependentLevel(level) ? this.parentValueFor(level, this.selectedTaxonomyChannel) : null
      }
      this.addValueText = ''
      this.addValueAbbrText = ''
      this.addValueDialog = true
    },
    confirmAddValue () {
      if (!this.canConfirmAddValue || !this.addValueField) {
        return
      }
      const name = this.addValueText.trim()
      // Track the new value so it appears in the dropdown; it is persisted to the
      // registry on save (the parent registers any value not already known). If
      // the name already exists it is only selected — the known abbreviation
      // stays authoritative over the one typed here.
      const level = this.addValueField.level
      const channel = this.addValueField.channel
      const parent = this.addValueField.parent
      if (!this.optionObjectsFor(level, channel).some(option => option.name === name)) {
        const value = { name: name, abbreviation: this.addValueAbbrText.trim() }
        if (parent !== null) {
          if (!this.extraTaxonomyOptionsByParent[level]) {
            this.$set(this.extraTaxonomyOptionsByParent, level, {})
          }
          if (!this.extraTaxonomyOptionsByParent[level][parent]) {
            this.$set(this.extraTaxonomyOptionsByParent[level], parent, [])
          }
          this.extraTaxonomyOptionsByParent[level][parent].push(value)
        } else {
          if (!this.extraTaxonomyOptions[level]) {
            this.$set(this.extraTaxonomyOptions, level, [])
          }
          this.extraTaxonomyOptions[level].push(value)
        }
      }
      this.channelTaxonomy[channel][level] = name
      this.addValueDialog = false
      this.addValueField = null
      this.addValueText = ''
      this.addValueAbbrText = ''
    },
    // Called by the parent on save. Returns the channel_taxonomy map to persist
    // (only channels with at least one value, plus entries preserved for channels
    // no longer present in the model config so an unrelated save never silently
    // deletes previously entered data) and the values not yet in the
    // channel_taxonomy_levels registry, grouped by level. Level values are
    // { name, abbreviation } objects (L6 stays a plain string); new values are
    // object[] for independent levels, { <parent>: object[] } for dependent ones.
    buildSaveData () {
      const taxonomyMap = {}
      const newTaxonomyValues = {}
      this.taxonomyChannelKeys.forEach(channel => {
        const row = this.channelTaxonomy[channel]
        const entry = {}
        this.taxonomySelectLevels.forEach(level => {
          const levelValue = String(row[level] || '').trim()
          if (levelValue.length > 0) {
            const value = {
              name: levelValue,
              abbreviation: this.abbreviationFor(level, channel, levelValue)
            }
            entry[level] = value
            if (this.isDependentLevel(level)) {
              // A dependent value with no parent in the row (legacy data) is
              // still saved on the model, but not registered — there is no
              // parent to nest it under in values_by_parent.
              const parent = this.parentValueFor(level, channel)
              if (parent) {
                const existing = ((this.taxonomyLevelOptions[level] || {})[parent]) || []
                if (!existing.some(option => option.name === levelValue)) {
                  if (!newTaxonomyValues[level]) {
                    newTaxonomyValues[level] = {}
                  }
                  if (!newTaxonomyValues[level][parent]) {
                    newTaxonomyValues[level][parent] = []
                  }
                  if (!newTaxonomyValues[level][parent].some(option => option.name === levelValue)) {
                    newTaxonomyValues[level][parent].push(value)
                  }
                }
              }
            } else {
              const existing = this.taxonomyLevelOptions[level] || []
              if (!existing.some(option => option.name === levelValue)) {
                if (!newTaxonomyValues[level]) {
                  newTaxonomyValues[level] = []
                }
                if (!newTaxonomyValues[level].some(option => option.name === levelValue)) {
                  newTaxonomyValues[level].push(value)
                }
              }
            }
          }
        })
        const freeText = String(row.L6 || '').trim()
        if (freeText.length > 0) {
          entry.L6 = freeText
        }
        if (Object.keys(entry).length > 0) {
          taxonomyMap[channel] = entry
        }
      })
      const existingTaxonomy = this.client.channel_taxonomy || {}
      Object.keys(existingTaxonomy).forEach(channel => {
        if (this.taxonomyChannelKeys.indexOf(channel) === -1) {
          taxonomyMap[channel] = existingTaxonomy[channel]
        }
      })
      return {
        taxonomyMap: taxonomyMap,
        newTaxonomyValues: newTaxonomyValues
      }
    }
  },
  computed: {
    // Levels selected from the shared registry; L6 is free text and excluded here.
    taxonomySelectLevels () {
      return ['L0', 'L1', 'L2', 'L3', 'L4', 'L5']
    },
    addValueAbbrHasSpaces () {
      return /\s/.test((this.addValueAbbrText || '').trim())
    },
    // Both the name and the abbreviation are required to add a value, and the
    // abbreviation cannot contain spaces.
    canConfirmAddValue () {
      return Boolean(this.addValueText && this.addValueText.trim() &&
        this.addValueAbbrText && this.addValueAbbrText.trim()) &&
        !this.addValueAbbrHasSpaces
    },
    taxonomyChannelKeys () {
      return Object.keys(this.channelTaxonomy).sort((a, b) =>
        this.channelDisplayName(a).localeCompare(this.channelDisplayName(b)))
    }
  },
  created: function () {
    // One row per media channel, pre-filled from the saved map.
    const media = (this.client.reference_model && this.client.reference_model.config && this.client.reference_model.config.media)
      ? this.client.reference_model.config.media
      : {}
    const savedTaxonomy = this.client.channel_taxonomy || {}
    const taxonomy = {}
    Object.keys(media).forEach(channel => {
      const saved = savedTaxonomy[channel] || {}
      const row = {}
      this.taxonomySelectLevels.forEach(level => {
        // Saved L0-L5 values are { name, abbreviation } objects; the row keeps
        // only the name and the abbreviation is looked up on display/save.
        const savedValue = saved[level]
        row[level] = (savedValue && typeof savedValue === 'object') ? String(savedValue.name || '') : ''
      })
      row.L6 = saved.L6 || ''
      taxonomy[channel] = row
    })
    this.channelTaxonomy = taxonomy
    this.selectedTaxonomyChannel = this.taxonomyChannelKeys.length > 0 ? this.taxonomyChannelKeys[0] : null
    // Available values per taxonomy level.
    this.loadChannelTaxonomyLevels().then(levels => {
      // Options and dependency flags are set together, atomically: the selects
      // stay disabled via taxonomyLoading until then, so a dependent level can
      // never render as independent mid-load.
      this.taxonomyLevelOptions = levels.values
      this.taxonomyDependsOnPrevious = levels.dependsOnPrevious || {}
    }).catch(error => {
      console.log('Error loading channel taxonomy level options', error)
    }).finally(() => {
      this.taxonomyLoading = false
    })
  }
}
</script>
